import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import DonutChart from "@/components/ui/pie-chart";
import { formatCurrency, numTrialDays, toCapital } from "@/utils/utils";
import { Check } from "lucide-react";
import { RecurringPlanState, RecurringStatusState, type SubscriptionInformationCardProps } from "../musio-plus";
import { useMemo } from "react";
import { MonthlyToAnnualDialog, AnnualToMonthlyDialog } from "./plan-change-dialog";
import CancelSubscription from "./cancel-subscription";
import usePaddle from "@/contexts/paddle/paddle-context";
import { NotificationRow } from "@/components/ui/notification-row";
import { useQuery } from "@tanstack/react-query";

export const SubscriptionInformationCard: React.FC<SubscriptionInformationCardProps> = ({ recurringStatus }) => {
  const trialing = recurringStatus.status == RecurringStatusState.Trialing;
  const numberTrialDays = numTrialDays(recurringStatus.periodEnd);
  const paddle = usePaddle();
  const monthlyPriceId = useMemo(() => (import.meta.env.VITE_REACT_APP_PADDLE_MUSIO_PLUS_MONTHLY), []);
  const annualPriceId = useMemo(() => (import.meta.env.VITE_REACT_APP_PADDLE_MUSIO_PLUS_ANNUAL), []);

  const { data: musioPlusPrices, isError: musioPlusPriceError } = useQuery({
    queryKey: ["musio-plus-prices"],
    queryFn: async () => {
      const response = await paddle?.PricePreview({
        items: [
          {
            priceId: monthlyPriceId,
            quantity: 1,
          },
          {
            priceId: annualPriceId,
            quantity: 1,
          },
        ],
      });

      return response!.data.details.lineItems;
    }
  });

  const monthlyPrice = musioPlusPrices?.at(0)?.formattedTotals.subtotal;
  const annualPrice = musioPlusPrices?.at(1)?.formattedTotals.subtotal;
  const hasUpcomingCharge = recurringStatus.status != RecurringStatusState.Paused && recurringStatus.pauses == undefined;
  const pauses = recurringStatus.pauses;

  return (
    <Card className="relative max-w-[373px] w-full flex flex-col space-y-8 justify-between bg-musio-bg p-4">
      <Check className="absolute top-5 right-2 h-3 w-3 font-extrabold text-black bg-musio-green rounded-full" />
      <CardHeader className="p-0">
        <CardTitle className="flex flex-col items-center align-middle justify-center">
          <p className="text-xl font-semibold text-center text-musio-green">Subscribed</p>
          <p className="text-center font-semibold text-lg text-lilac">{toCapital(recurringStatus.plan!)}</p>

          {pauses ?
            <p className="text-center font-normal text-sm text-musio-orange">
              Pauses: {pauses}
            </p> :
            hasUpcomingCharge ?
              <>
                <p className="text-center font-normal text-sm text-lilac">
                  Upcoming Charge: {formatCurrency(recurringStatus?.amount!, recurringStatus?.currency!)}
                </p>
                <p className="text-center font-normal text-[10px]" style={{ color: "rgba(132, 121, 143, 1)" }}>
                  On {recurringStatus.periodEnd}
                </p>
              </>
              : null
          }

        </CardTitle>
      </CardHeader>

      {trialing &&
        <CardContent className="flex flex-row items-center justify-center p-0 gap-x-2">
          <p className="text-sm font-normal text-musio-orange">{numberTrialDays} days left in trial</p>
          <DonutChart daysLeft={numberTrialDays} totalDays={7} width={18} height={18} />
        </CardContent>
      }

      <CardFooter className="flex flex-col gap-0.5 p-0">
        <div className="flex flex-col space-y-2">
          {recurringStatus.plan === RecurringPlanState.Monthly ? (
            <MonthlyToAnnualDialog showProrate={!trialing /* opposite of trialing */} />
          ) : (
            <AnnualToMonthlyDialog />
          )}

          {musioPlusPriceError && annualPrice && monthlyPrice ? (
            <p className="text-center font-semibold text-xl text-lilac">
              {recurringStatus.plan === RecurringPlanState.Monthly
                ? `Annual Billing - ${annualPrice}`
                : `${monthlyPrice} / Month`}
            </p>
          ) : null}

          {musioPlusPriceError ? (
            <NotificationRow intent="error">
              {musioPlusPriceError}
            </NotificationRow>
          ) : null}
        </div>
        <CancelSubscription />
      </CardFooter>
    </Card>
  );
};
